import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaFilePdf, FaDownload, FaLock, FaUnlock, FaShieldAlt, FaEyeSlash, FaCompressAlt } from 'react-icons/fa';
import API_CONFIG from '../config/api';
import { analytics } from '../utils/analytics';
import compressionUtils from '../utils/compression';
import CompressionModal from './CompressionModal';

const PdfUnlocker = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [unlockedFile, setUnlockedFile] = useState(null);
  const [showCompressionModal, setShowCompressionModal] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError('');
      setSuccess('');
      setUnlockedFile(null);
      analytics.pdfUnlocker.fileSelected(file.name, Math.round(file.size / 1024));
    } else {
      setError('Please select a valid PDF file.');
      setSelectedFile(null);
      analytics.pdfUnlocker.fileSelectionError('invalid_file_type');
    }
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a PDF file.');
      analytics.pdfUnlocker.unlockAttemptError('no_file_selected');
      return;
    }
    
    if (!password.trim()) {
      setError('Please enter the password.');
      analytics.pdfUnlocker.unlockAttemptError('no_password');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');
    
    analytics.pdfUnlocker.unlockAttempt(selectedFile.name);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('password', password);

      const response = await fetch(API_CONFIG.getPdfUnlockUrl(), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const unlockedPdfBlob = new Blob([blob], { type: 'application/pdf' });
      setUnlockedFile(unlockedPdfBlob);
      setSuccess('PDF unlocked successfully! You can now download it.');
      analytics.pdfUnlocker.unlockSuccess(selectedFile.name, Math.round(blob.size / 1024));
    } catch (error) {
      console.error('Error unlocking PDF:', error);
      setError('Failed to unlock PDF. Please check your password and try again.');
      analytics.pdfUnlocker.unlockError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    if (unlockedFile) {
      compressionUtils.downloadFile(unlockedFile, selectedFile.name, 'unlocked');
      analytics.pdfUnlocker.downloadUnlockedPdf(selectedFile.name);
    }
  };

  const handleCompress = async () => {
    if (!unlockedFile) return;

    setIsCompressing(true);
    setShowCompressionModal(false);

    try {
      await compressionUtils.compressAndDownload(
        unlockedFile, 
        selectedFile.name, 
        'high', 
        'unlocked_compressed'
      );
      
      setSuccess('Compressed PDF downloaded successfully!');
    } catch (error) {
      console.error('Compression error:', error);
      setError('Failed to compress PDF. Please try downloading the original version.');
    } finally {
      setIsCompressing(false);
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          {/* Security Notice Card */}
          <Card className="mb-4 border-success">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <FaShieldAlt className="text-success me-2" size={24} />
                <h5 className="mb-0 text-success">100% Secure & Private</h5>
              </div>
              <p className="mb-2">
                <strong>Your PDF files are never stored on our servers.</strong>
              </p>
              <p className="mb-0 small text-muted">
                Files are processed in memory and immediately deleted. We cannot access your content.
              </p>
            </Card.Body>
          </Card>

          <Card className="shadow">
            <Card.Header className="bg-primary text-white text-center">
              <h3 className="mb-0">
                <FaLock className="me-2" />
                PDF Unlocker
              </h3>
            </Card.Header>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaFilePdf className="me-2" />
                    Select PDF File
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    isInvalid={!!error && !selectedFile}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please select a PDF file.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <FaLock className="me-2" />
                    Password
                  </Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Enter PDF password"
                    value={password}
                    onChange={handlePasswordChange}
                    isInvalid={!!error && !password}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please enter the password.
                  </Form.Control.Feedback>
                </Form.Group>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" className="mb-3">
                    {success}
                  </Alert>
                )}

                <div className="d-grid gap-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isLoading || !selectedFile || !password}
                  >
                    {isLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Unlocking PDF...
                      </>
                    ) : (
                      <>
                        <FaUnlock className="me-2" />
                        Unlock PDF
                      </>
                    )}
                  </Button>

                  {unlockedFile && (
                    <>
                      <div className="d-grid gap-2">
                        <Button
                          variant="success"
                          size="lg"
                          onClick={handleDownload}
                          disabled={isCompressing}
                        >
                          <FaDownload className="me-2" />
                          Download Original ({compressionUtils.getFileSize(unlockedFile.size)})
                        </Button>
                        
                        <Button
                          variant="info"
                          size="lg"
                          onClick={() => setShowCompressionModal(true)}
                          disabled={isCompressing}
                        >
                          <FaCompressAlt className="me-2" />
                          Download Compressed Version
                        </Button>
                      </div>
                      
                      {isCompressing && (
                        <div className="text-center mt-3">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Compressing PDF...
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="mt-4 shadow-sm">
            <Card.Body>
              <h5>How to use:</h5>
              <ol>
                <li>Select your password-protected PDF file</li>
                <li>Enter the password for the PDF</li>
                <li>Click "Unlock PDF" to process the file</li>
                <li>Choose to download original or compressed version</li>
              </ol>
              
              <hr />
              
              <div className="d-flex align-items-start">
                <FaEyeSlash className="text-muted me-2 mt-1" />
                <div>
                  <h6 className="mb-2">Privacy & Security</h6>
                  <ul className="small text-muted mb-0">
                    <li>Files are processed securely and never stored</li>
                    <li>All data is encrypted during transmission</li>
                    <li>We cannot access or view your PDF content</li>
                    <li>Files are automatically deleted after processing</li>
                  </ul>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Compression Modal */}
      <CompressionModal
        show={showCompressionModal}
        onHide={() => setShowCompressionModal(false)}
        onCompress={handleCompress}
        fileName={selectedFile?.name}
        fileSize={unlockedFile?.size}
        isCompressing={isCompressing}
      />
    </Container>
  );
};

export default PdfUnlocker; 